"use client";

import type { ReactNode } from "react";
import { Suspense, useEffect } from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { store, persistor } from "@/store";
import { AuthProvider } from "@/platforms/customer/context/AuthContext";
import SupplierPortalShell from "@/platforms/supplier/layouts/SupplierPortalShell";
import { ComparisonProvider } from "@/context/ComparisonContext";
import ComparisonStickyBar from "@/platforms/customer/components/ComparisonStickyBar";
import { setCartItems, loadCartFromStorage } from "@/store/cartSlice";
import { setWishlistItems, loadWishlistFromStorage } from "@/store/wishlistSlice";

function ClientHydrator() {
  useEffect(() => {
    store.dispatch(setCartItems(loadCartFromStorage()));
    store.dispatch(setWishlistItems(loadWishlistFromStorage()));
  }, []);
  return null;
}

/**
 * PersistGate starts bootstrapped=false and renders `loading` until rehydrate.
 * With loading={null}, the entire App Router tree was omitted from SSR HTML
 * (View Source showed only B:0 / $RC). Use children as loading so crawlers
 * get real markup; AuthProvider stays outside so SSR still has auth context.
 *
 * Suspense wraps the tree so pages using useSearchParams (via useAppLocation)
 * satisfy Next.js static generation / CSR-bailout requirements.
 */
export default function Providers({ children }: { children: ReactNode }) {
  return (
    <Provider store={store}>
      <AuthProvider>
        <Suspense fallback={null}>
          <ComparisonProvider>
            <SupplierPortalShell>
              <PersistGate loading={children} persistor={persistor}>
                <ClientHydrator />
                {children}
              </PersistGate>
            </SupplierPortalShell>
            <ComparisonStickyBar />
          </ComparisonProvider>
        </Suspense>
        <ToastContainer position="top-right" autoClose={3000} />
      </AuthProvider>
    </Provider>
  );
}
